(() => {
  if (customElements.get('collection-description-disclosure')) return;

  class CollectionDescriptionDisclosure extends HTMLElement {
    connectedCallback() {
      this.frame = requestAnimationFrame(() => this.initialize());
    }

    initialize() {
      if (!this.isConnected || this.controller) return;
      this.content = this.querySelector('.collection-description-disclosure__content');
      this.button = this.querySelector('.collection-description-disclosure__toggle');
      if (!this.content || !this.button) return;
      this.controller = new AbortController();
      this.mobile = matchMedia('(max-width: 767px)');
      this.expanded = false;
      this.links = new Map([...this.content.querySelectorAll('a')].map(link => [link, link.getAttribute('tabindex')]));
      this.button.addEventListener('click', () => {
        this.expanded = !this.expanded;
        this.refresh();
        if (!this.expanded && this.getBoundingClientRect().bottom < 0) this.scrollIntoView({ block: 'start' });
      }, { signal: this.controller.signal });
      this.onViewportChange = () => this.refresh();
      this.mobile.addEventListener('change', this.onViewportChange);
      this.resize = new ResizeObserver(entries => {
        const width = entries[0].contentRect.width;
        if (Math.abs(width - (this.lastWidth || 0)) < 0.5) return;
        this.lastWidth = width;
        this.refresh();
      });
      this.resize.observe(this);
      this.refresh();
      document.fonts.ready.then(() => { if (this.isConnected && this.controller) this.refresh(); });
    }

    refresh() {
      this.removeAttribute('data-collapsed');
      const style = getComputedStyle(this.content);
      const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.6;
      const configured = Number(this.getAttribute('data-preview-lines-desktop'));
      const desktopLines = Number.isInteger(configured) && configured >= 1 && configured <= 10 ? configured : 0;
      const previewLines = this.mobile.matches ? 4 : desktopLines;
      const previewHeight = lineHeight * (previewLines || 4);
      const hasOverflow = this.content.scrollHeight > previewHeight + 2;
      const canCollapse = previewLines > 0 && hasOverflow;
      const collapsed = canCollapse && !this.expanded;
      this.style.setProperty('--description-preview-height', `${previewHeight}px`);
      if (collapsed) this.setAttribute('data-collapsed', 'true');
      this.button.hidden = !canCollapse;
      this.button.setAttribute('aria-expanded', String(!collapsed));
      this.button.textContent = collapsed ? 'Read more' : 'Read less';
      // Clipped links become keyboard-accessible again when the full text is shown.
      for (const [link, tabindex] of this.links) {
        if (collapsed) link.setAttribute('tabindex', '-1');
        else if (tabindex === null) link.removeAttribute('tabindex');
        else link.setAttribute('tabindex', tabindex);
      }
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.resize?.disconnect();
      this.controller?.abort();
      this.mobile?.removeEventListener('change', this.onViewportChange);
      if (this.content) {
        this.expanded = true;
        this.refresh();
      }
      this.controller = null;
    }
  }

  customElements.define('collection-description-disclosure', CollectionDescriptionDisclosure);
})();
